import { fetchAssessmentOutcomesData } from './query/assessmentOutcomesAPI';
import { AssessmentOutcomesAPIResponseInterface } from '@/features/dashboard/models/AssessmentOutcomesWidget';
import { findAssessmentWindow } from '@/features/dashboard/utils/findAssessmentWindow';
import { Student } from '@/models';
import { ASSESSMENT_TYPE_FILTERS } from '@/constants/constants';
import StudentRecordService from './StudentRecordService';
import { extractScoreFromAssessment } from '@/components/Reports/util/extractScore';
import { getPercentileInfoUsingScaledCutlines } from '@/components/Reports/util/cutscores';
import SRS from './StudentRecordService';
import store from '@/store';
import { ScoreType } from '@/enums/ScoreType';
import { GradeNumericToString } from '@/enums/Grade';

// Feature flag to control which implementation to use
// Set to true to use new implementation that leverages class progress report logic
const USE_CLASS_PROGRESS_LOGIC = true;

interface AssessmentOutcomesServiceParams {
  classroomId: number;
  students: Student[];
  assessmentWindows: any[];
  assessmentCalendar: any;
  districtId?: number;
  schoolId?: number;
  grade?: number;
}

/**
 * Service that generates assessment outcomes data using class progress report logic
 * Transforms the data to match the exact format expected by the assessment outcomes widget
 */
export class AssessmentOutcomesService {
  
  /**
   * Main entry point - decides whether to use old or new implementation
   */
  static async fetchAssessmentOutcomesData(params: { 
    classroomId: number;
    students?: Student[];
    assessmentWindows?: any[];
    assessmentCalendar?: any;
    districtId?: number;
    schoolId?: number;
    grade?: number;
  }): Promise<AssessmentOutcomesAPIResponseInterface> {
    if (!USE_CLASS_PROGRESS_LOGIC) {
      // Use original implementation
      return await fetchAssessmentOutcomesData({ classroomId: params.classroomId }) as AssessmentOutcomesAPIResponseInterface;
    }
    
    // Use new class progress report logic
    return await this.fetchAssessmentOutcomesWithClassProgressLogic(params);
  }

  /**
   * New implementation using class progress report logic
   */
  private static async fetchAssessmentOutcomesWithClassProgressLogic(params: { 
    classroomId: number;
    students?: Student[];
    assessmentWindows?: any[];
    assessmentCalendar?: any;
    districtId?: number;
    schoolId?: number;
    grade?: number;
  }): Promise<AssessmentOutcomesAPIResponseInterface> {
    let students = params.students || [];
    let assessmentWindows = params.assessmentWindows || [];
    let assessmentCalendar = params.assessmentCalendar || {};
    let districtId = params.districtId;
    let schoolId = params.schoolId;
    let grade = params.grade;
    
    // If no data is provided, fetch it ourselves
    if (students.length === 0 || assessmentWindows.length === 0) {
      
      try {
        // Fetch roster data to get students, districtId, schoolId
        const rosterData = await this.fetchRosterData(params.classroomId);
        if (rosterData) {
          students = rosterData.students || [];
          districtId = rosterData.districtId;
          schoolId = rosterData.schoolId;
          grade = rosterData.grade;
        }
        
        // Fetch assessment calendar and windows
        const calendarData = await this.fetchAssessmentCalendarData();
        if (calendarData) {
          assessmentCalendar = calendarData.calendar || {};
          assessmentWindows = calendarData.windows || [];
        }
        
      } catch (error) {
        console.error('❌ Failed to fetch missing data:', error);
        // Throw error instead of falling back to old API when feature flag is true
        throw new Error(`Failed to fetch required data for new assessment outcomes implementation: ${error}`);
      }
    }
    
    if (students.length === 0) {
      throw new Error('No students found for assessment outcomes generation. Cannot proceed with new implementation.');
    }
    
    return await this.generateOutcomesData({
      classroomId: params.classroomId,
      students,
      assessmentWindows,
      assessmentCalendar,
      districtId,
      schoolId,
      grade
    });
  }

  /**
   * Get the score type from user entitlements
   */
  private static getScoreType(): ScoreType {
    const state = store.getState();
    return state.userEntitlement?.data?.scoreType || ScoreType.ARM;
  }

  /**
   * Get the appropriate score field name based on score type
   */
  private static getScoreFieldName(scoreType: ScoreType): string {
    return scoreType === ScoreType.ISIP ? 'ISIP_overall_universal' : 'ARM';
  }

  /**
   * Get the appropriate scale parameter for scaled cutlines based on score type
   */
  private static getScaleParameter(scoreType: ScoreType): string {
    return scoreType === ScoreType.ISIP ? 'ISIP_overall_universal' : 'ARM';
  }

  /**
   * Generate assessment outcomes data using class progress report logic
   */
  private static async generateOutcomesData(params: AssessmentOutcomesServiceParams): Promise<AssessmentOutcomesAPIResponseInterface> {
    const { classroomId, students, assessmentWindows, assessmentCalendar, districtId, schoolId, grade } = params;
    
    // Transform assignment windows to the format findAssessmentWindow expects
    const transformedWindows = this.transformAssignmentWindowsToPeriodItems(assessmentWindows, grade);
    
    // Determine if we're showing benchmark windows or progress monitoring periods
    const assessmentWindowStatus = findAssessmentWindow(transformedWindows);
    const isBenchmarkWindow = this.isBenchmarkWindow(assessmentWindowStatus);
    
    let graphData: any[] = [];
    
    if (isBenchmarkWindow) {
      // Show BOY/MOY/EOY benchmark data
      graphData = await this.generateBenchmarkWindowData(students, assessmentWindows, districtId, schoolId, grade, assessmentCalendar);
    } else {
      // Show current period + 2 previous periods
      graphData = await this.generatePeriodData(students, assessmentCalendar, assessmentWindowStatus, districtId, schoolId, grade);
    }
    
    // Transform to assessment outcomes format
    return this.transformToAssessmentOutcomesFormat(graphData);
  }

  /**
   * Transform assignment windows to AssessmentPeriodItem format for findAssessmentWindow
   */
  private static transformAssignmentWindowsToPeriodItems(assignmentWindows: any[], grade?: number): any[] {
    if (!assignmentWindows || assignmentWindows.length === 0) {
      return [];
    }
    
    // Filter assignments for the current grade and transform to period items
    const gradeString = grade !== undefined && grade !== null ? GradeNumericToString[grade.toString()] : null;
    const relevantAssignments = assignmentWindows.filter(window => {
      // Include if no grade specified or if grade matches
      return !gradeString || window.grade === gradeString || this.isGradeMatch(window.grade, grade);
    });
    
    console.log('🔄 Transforming assignment windows:', {
      totalWindows: assignmentWindows.length,
      gradeString,
      relevantAssignments: relevantAssignments.length,
      sampleAssignment: relevantAssignments[0]
    });
    
    return relevantAssignments.map(assignment => ({
      // Transform assignment structure to AssessmentPeriodItem structure
      assignmentType: assignment.assignmentType, // 'BENCHMARK', etc.
      startDate: assignment.dateFrom.split(' ')[0], // Extract date part: '2025-07-08'
      endDate: assignment.dateTo.split(' ')[0], // Extract date part: '2025-07-22'
      periodId: assignment.periodId ? parseInt(assignment.periodId) : null,
      tags: assignment.tags || [],
      screeningWindowType: assignment.assignmentType === 'BENCHMARK' ? 'DistrictAssignment' : 'Period',
      current: this.isCurrentWindow(assignment.dateFrom, assignment.dateTo),
      // Add other properties that findAssessmentWindow might need
      entityType: assignment.entityType,
      grade: assignment.grade,
      status: assignment.status
    }));
  }

  /**
   * Check if a window is currently active
   */
  private static isCurrentWindow(dateFrom: string, dateTo: string): boolean {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const startDate = new Date(dateFrom.split(' ')[0]);
    const endDate = new Date(dateTo.split(' ')[0]);
    startDate.setHours(0, 0, 0, 0);
    endDate.setHours(23, 59, 59, 999);
    
    return today >= startDate && today <= endDate;
  }



  /**
   * Check if assignment grade matches the student grade
   */
  private static isGradeMatch(assignmentGrade: string, studentGrade?: number): boolean {
    if (!studentGrade && studentGrade !== 0) return true; // Include all if no student grade
    
    const expectedGradeString = studentGrade !== undefined && studentGrade !== null ? GradeNumericToString[studentGrade.toString()] : null;
    return assignmentGrade === expectedGradeString;
  }

  /**
   * Check if the current assessment window is a benchmark window
   */
  private static isBenchmarkWindow(assessmentWindowStatus: any): boolean {
    
    if (!assessmentWindowStatus?.assessmentWindowType) return false;
    
    const isBenchmark = assessmentWindowStatus.assessmentWindowType === 'BENCHMARK';
    const hasBenchmarkTags = assessmentWindowStatus.tags?.some((tag: string) => 
      ['BOY', 'MOY', 'EOY', 'WINDOW_BOY', 'WINDOW_MOY', 'WINDOW_EOY'].includes(tag)
    );
    
    const result = isBenchmark && hasBenchmarkTags;
    
    return result;
  }

  /**
   * Generate data for benchmark windows (BOY/MOY/EOY)
   */
  private static async generateBenchmarkWindowData(
    students: Student[], 
    assessmentWindows: any[], 
    districtId?: number, 
    schoolId?: number, 
    grade?: number,
    assessmentCalendar?: any
  ): Promise<any[]> {
    const studentIds = students.map(s => s.id);
    const benchmarkWindows = ['BOY', 'MOY', 'EOY'];
    
    // Fetch all benchmark data in parallel for better performance
    const windowPromises = benchmarkWindows.map(async (windowId) => {
      
      try {
        // Get most recent benchmark score for this window
        const benchmarkActivities = await StudentRecordService.getLatestAssessments({
          studentIds,
          locale: 'EN_US',
          equatedScores: false,
          testType: ASSESSMENT_TYPE_FILTERS.BENCHMARK,
          screeningWindow: windowId,
          dateStart: undefined,
          dateEnd: undefined,
          periodId: undefined
        });
        
        if (benchmarkActivities.length > 0) {
          // Get the period for this window from assessment calendar
          const windowPeriodId = this.getPeriodIdForWindow(windowId, assessmentWindows, assessmentCalendar);
          const windowPerformanceStats = await this.calculatePerformanceStats(
            benchmarkActivities, 
            windowId, 
            windowPeriodId,
            districtId,
            schoolId,
            grade
          );
          return {
            period: this.getBenchmarkWindowLabel(windowId),
            performanceScaleStats: windowPerformanceStats
          };
        } else {
          console.log(`❌ No ${windowId} activities found`);
          return null;
        }
      } catch (error) {
        console.error(`❌ Error fetching ${windowId} data:`, error);
        return null;
      }
    });
    
    // Wait for all windows to complete and filter out null results
    const results = await Promise.all(windowPromises);
    return results.filter(result => result !== null);
  }

  /**
   * Generate data for progress monitoring periods (current + 2 previous)
   */
  private static async generatePeriodData(
    students: Student[], 
    assessmentCalendar: any, 
    assessmentWindowStatus: any,
    districtId?: number,
    schoolId?: number,
    grade?: number
  ): Promise<any[]> {
    const studentIds = students.map(s => s.id);
    const currentPeriodId = assessmentWindowStatus?.currentPeriodIdentifier;
    
    if (!assessmentCalendar?.periods) {
      console.log('❌ No assessment calendar periods available');
      return [];
    }
    
    // If no current period, use the last period as current
    let targetPeriodIndex;
    if (currentPeriodId) {
      targetPeriodIndex = assessmentCalendar.periods.findIndex(
        (period: any) => period.periodIdentifier === currentPeriodId
      );
    } else {
      // Use the last period as current
      targetPeriodIndex = assessmentCalendar.periods.length - 1;
      console.log('📅 No current period found, using last period as current');
    }
    
    if (targetPeriodIndex === -1) {
      targetPeriodIndex = assessmentCalendar.periods.length - 1;
      console.log('📅 Current period not found, using last period');
    }
    
    // Get current period + 2 previous periods
    const periodsToShow: any[] = [];
    for (let i = Math.max(0, targetPeriodIndex - 2); i <= targetPeriodIndex; i++) {
      periodsToShow.push(assessmentCalendar.periods[i]);
    }
    
    // Fetch all period data in parallel for better performance
    const periodPromises = periodsToShow.map(async (period) => {
      
      try {
        // Get most recent score of any type for this period
        const periodActivities = await StudentRecordService.getLatestAssessments({
          studentIds,
          locale: 'EN_US',
          equatedScores: false,
          testType: null, // Any type
          periodId: period.periodIdentifier,
          dateStart: undefined,
          dateEnd: undefined,
          screeningWindow: undefined
        });
        
        if (periodActivities.length > 0) {
          const periodPerformanceStats = await this.calculatePerformanceStats(
            periodActivities, 
            period.name, 
            period.periodIdentifier,
            districtId,
            schoolId,
            grade
          );
          return {
            period: period.name,
            performanceScaleStats: periodPerformanceStats,
            periodIdentifier: period.periodIdentifier // Keep for sorting
          };
        } else {
          console.log(`❌ No activities found for ${period.name}`);
          return null;
        }
      } catch (error) {
        console.error(`❌ Error fetching ${period.name} data:`, error);
        return null;
      }
    });
    
    // Wait for all periods to complete and filter out null results
    const results = await Promise.all(periodPromises);
    const periodData = results.filter(result => result !== null);
    
    // Sort periods chronologically (by periodIdentifier)
    return periodData.sort((a, b) => {
      return (a.periodIdentifier || 0) - (b.periodIdentifier || 0);
    }).map(({ periodIdentifier, ...rest }) => rest); // Remove periodIdentifier from final result
  }

  /**
   * Get period ID for a benchmark window
   */
  private static getPeriodIdForWindow(windowId: string, assessmentWindows: any[], assessmentCalendar: any): number {
    // Find the assessment window that matches this benchmark window
    const matchingWindow = assessmentWindows.find(window => {
      const tags = window.tags || [];
      return tags.some((tag: string) => tag.includes(windowId));
    });
    
    if (matchingWindow?.periodId !== null && matchingWindow?.periodId !== undefined) {
      return parseInt(matchingWindow.periodId);
    }
    
    // No fallbacks - if we can't find the period ID, we should error
    throw new Error(`Cannot find period ID for benchmark window ${windowId}. Assessment window must have a valid periodId.`);
  }

    // Cache for scaled cutlines to avoid redundant API calls
  private static scaledCutlinesCache: { [key: string]: any[] } = {};

  /**
   * Calculate performance statistics from activities using scaled cutlines
   */
  private static async calculatePerformanceStats(
    activities: any[], 
    windowOrPeriodName: string, 
    periodId?: number,
    districtId?: number,
    schoolId?: number,
    grade?: number
  ): Promise<any[]> {
    
    // Get score type from user entitlements
    const scoreType = this.getScoreType();
    const scoreFieldName = this.getScoreFieldName(scoreType);
    const scaleParameter = this.getScaleParameter(scoreType);
    
    // Get scaled cutlines for this period like other reports do
    let scaledCutlines: any[] = [];
    
    if (districtId && schoolId && grade !== undefined && periodId !== undefined) {
      // Create cache key including score type
      const cacheKey = `${districtId}-${schoolId}-${grade}-${periodId}-${scaleParameter}`;
      
      // Check cache first
      if (this.scaledCutlinesCache[cacheKey]) {
        scaledCutlines = this.scaledCutlinesCache[cacheKey];
      } else {
        try {
          const cutlines = await (SRS as any).getScaledCutlines({
            districtId,
            schoolId,
            grade,
            scale: scaleParameter, // Use appropriate scale based on score type
            periodId: [periodId] // API expects array
          });
          
          if (Array.isArray(cutlines)) {
            scaledCutlines = cutlines;
            // Cache the result
            this.scaledCutlinesCache[cacheKey] = cutlines;
          } else {
            console.warn('❌ API returned non-array cutlines:', cutlines);
          }
        } catch (error) {
          console.warn('❌ Could not fetch scaled cutlines:', error);
        }
      }
    } else {
      console.warn('❌ Missing required parameters for scaled cutlines:', { 
        hasDistrictId: !!districtId, 
        hasSchoolId: !!schoolId, 
        hasGrade: grade !== undefined, 
        hasPeriodId: periodId !== undefined 
      });
    }
    
    // Group activities by performance level using scaled cutlines
    const performanceCounts: { [key: string]: { count: number; color: string; label: string } } = {};
    
    // Process each activity and categorize by actual PR value using scaled cutlines
    activities.forEach(activity => {
      const { score, PR } = extractScoreFromAssessment(activity, scoreFieldName, scoreFieldName);
      
      if (PR !== null && PR !== undefined) {
        let performanceLevel: string;
        let color: string;
        let label: string;
        
        if (scaledCutlines.length > 0) {
          // Use scaled cutlines to determine performance level
          const percentileInfo = getPercentileInfoUsingScaledCutlines(PR, scaledCutlines);
          
          if (percentileInfo && percentileInfo.color && percentileInfo.label) {
            // Use the actual cutline label and color
            performanceLevel = percentileInfo.label;
            color = percentileInfo.color;
            label = percentileInfo.label;
          } else {
            // Skip this activity if we can't get proper cutline info
            return;
          }
        } else {
          // Skip this activity if no scaled cutlines available
          return;
        }
        
        if (!performanceCounts[performanceLevel]) {
          performanceCounts[performanceLevel] = { count: 0, color, label };
        }
        performanceCounts[performanceLevel].count++;
      }
    });
    
    // Convert to the format expected by assessment outcomes widget and sort by performance level
    const performanceStats = Object.entries(performanceCounts)
      .map(([level, data]) => ({
        label: data.label,
        value: level,
        count: data.count,
        color: data.color
      }))
      .sort((a, b) => {
        // Sort by the actual percentile ranges that the cutlines represent
        // Higher performance levels should come first for proper stacking
        if (scaledCutlines.length > 0) {
          // Find the cutlines for each level to determine proper order
          const cutlineA = scaledCutlines.find(c => c.cutoffLabel === a.value);
          const cutlineB = scaledCutlines.find(c => c.cutoffLabel === b.value);
          
          if (cutlineA && cutlineB && cutlineA.percentile !== null && cutlineB.percentile !== null) {
            // Sort by percentile ascending (lower percentiles first = bottom of stack, higher percentiles last = top of stack)
            const result = (cutlineA.percentile || 0) - (cutlineB.percentile || 0);
            console.log(`📊 Sort result: ${cutlineA.percentile} - ${cutlineB.percentile} = ${result} (${cutlineA.cutoffLabel} vs ${cutlineB.cutoffLabel})`);
            return result;
          }
        }
        
                 // Fallback: Define a standard order for common cutline labels (lowest to highest performance)
         const standardOrder = ['Catch Up', 'Below', 'On Track', 'Above'];
         const indexA = standardOrder.indexOf(a.value);
         const indexB = standardOrder.indexOf(b.value);
         
         if (indexA !== -1 && indexB !== -1) {
           return indexA - indexB;
         }
        
        // Final fallback: sort alphabetically by label for consistent ordering
        return a.label.localeCompare(b.label);
      });
    
    // If we have no performance stats (because no scaled cutlines were available), throw an error
    if (performanceStats.length === 0 && activities.length > 0) {
      throw new Error(`No scaled cutlines available for ${windowOrPeriodName}. Cannot generate performance statistics.`);
    }
    
    return performanceStats;
  }

  /**
   * Get benchmark window label
   */
  private static getBenchmarkWindowLabel(windowId: string): string {
    const labels: { [key: string]: string } = {
      'BOY': 'BOY',
      'MOY': 'MOY',
      'EOY': 'EOY'
    };
    return labels[windowId] || windowId;
  }

  /**
   * Fetch roster data for a classroom
   */
  private static async fetchRosterData(classroomId: number): Promise<{
    students: Student[];
    districtId: number;
    schoolId: number;
    grade: number;
  } | null> {
    try {
      // Import the roster service dynamically to avoid circular dependencies
      const { default: store } = await import('@/store');
      const state = store.getState();
      
      const roster = state.reports?.roster;
      if (!roster?.data) {
        console.warn('❌ No roster data available in store');
        return null;
      }
      
      // Get students for this classroom
      const allStudents = roster.data.student || [];
      const classroomStudents = allStudents.filter((student: any) => 
        student.classroom_id?.find((id: any) => `${id}` === `${classroomId}`)
      );
      
      // Get district and school info
      const districtId = roster.data.district?.[0]?.id;
      const schoolId = roster.ui?.selections?.school?.id;
      
      // Calculate predominant grade
      const { getMostCommonGradeNumber } = await import('@/utils/gradeUtils');
      const grade = getMostCommonGradeNumber(classroomStudents);
      
      return {
        students: classroomStudents,
        districtId,
        schoolId,
        grade
      };
    } catch (error) {
      console.error('❌ Error fetching roster data:', error);
      return null;
    }
  }

  /**
   * Fetch assessment calendar and windows data
   */
  private static async fetchAssessmentCalendarData(): Promise<{
    calendar: any;
    windows: any[];
  } | null> {
    try {
      // Import store dynamically to avoid circular dependencies
      const { default: store } = await import('@/store');
      const state = store.getState();
      
      const calendar = state.calendar;
      if (!calendar) {
        console.warn('❌ No calendar data available in store');
        return null;
      }
      
      // Get assessment windows from the correct structure
      const districtWindows = calendar.assessmentWindows?.districtAssignments || [];
      const schoolWindows = calendar.assessmentWindows?.schoolAssignments || [];
      const allWindows = [...districtWindows, ...schoolWindows];
      
      return {
        calendar: calendar.assessmentCalendar,
        windows: allWindows
      };
    } catch (error) {
      console.error('❌ Error fetching calendar data:', error);
      return null;
    }
  }

  /**
   * Transform data to match assessment outcomes widget format
   */
  private static transformToAssessmentOutcomesFormat(graphData: any[]): AssessmentOutcomesAPIResponseInterface {
    // Extract unique performance levels and their colors from the graph data
    const allLevels = new Set<string>();
    const levelColors: { [key: string]: string } = {};
    
    graphData.forEach(periodData => {
      periodData.performanceScaleStats?.forEach((stat: any) => {
        allLevels.add(stat.value);
        if (stat.color) {
          levelColors[stat.value] = stat.color;
        }
      });
    });
    
    // Create levels configuration from actual data
    const levels = Array.from(allLevels).map(level => ({
      label: { 
        labelEN: level, // Use the level value directly since it comes from scaled cutlines
        labelES: level 
      },
      value: level,
      color: levelColors[level] || '#CCCCCC'
    }));
    
    const transformedData = {
      classroomAssessmentOutcomeData: {
        graphData: graphData.map(periodData => ({
          period: periodData.period,
          performanceScaleStats: periodData.performanceScaleStats.map((stat: any) => ({
            label: stat.label,
            value: stat.value,
            count: stat.count
          }))
        })),
        configsUsed: {
          cutlineLabel: 'Performance Levels',
          cutlineType: 'PERCENTILE',
          levels: levels
        }
      }
    };
    
    return transformedData;
  }
}

// Export the feature flag for easy toggling
export { USE_CLASS_PROGRESS_LOGIC };

// Export the main function to maintain compatibility
export const fetchAssessmentOutcomesDataWithClassProgressLogic = AssessmentOutcomesService.fetchAssessmentOutcomesData; 